import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import { threeQuestions } from '@/mocks/homeData';

export default function Section3Questions({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-24 left-20 w-[380px] h-[380px] rounded-full bg-primary-100/15 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 right-16 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/3 left-1/2 w-[280px] h-[280px] rounded-full bg-secondary-100/8 blur-[150px] pointer-events-none animate-orb-float-1" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50/80 border border-accent-200/40 text-sm font-body font-semibold text-accent-600 tracking-[0.1em] mb-3 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <i className="ri-questionnaire-line text-sm" />
            核心议题
          </span>
          <h2
            className={`font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            今天从<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">三个问题</span>展开
          </h2>
          <p
            className={`text-sm md:text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            AI能不能把原本分散在课堂视频、语音、课件、学生回应、教学设计、专家意见里的信息，整理成一套可以回看、可以解释、可以用于评价和改进的证据？
          </p>
        </div>

        {/* 三问题卡片 */}
        <div className="relative mb-8 md:mb-10">
          {/* 桌面端连接线 */}
          <div className="hidden lg:block absolute top-[104px] left-[16%] right-[16%] h-px">
            <div className="w-full h-full bg-gradient-to-r from-primary-300/0 via-primary-300/40 to-primary-300/0" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {threeQuestions.map((q, i) => {
              const isAccent = q.color === 'accent';
              const chainLabels = ['可信', '原理', '应用'];
              return (
                <div
                  key={q.id}
                  className={`group relative transition-all duration-700 ${
                    titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${300 + i * 180}ms` }}
                >
                  {/* 卡片主体 */}
                  <div className="relative rounded-xl glass-liquid overflow-hidden hover:border-gold-300/40 transition-all duration-300 hover:-translate-y-1">
                    {/* 顶部渐变条 */}
                    <div
                      className="h-1 w-full"
                      style={{
                        background: isAccent
                          ? 'linear-gradient(90deg, oklch(var(--accent-300)), oklch(var(--accent-500)))'
                          : 'linear-gradient(90deg, oklch(var(--primary-300)), oklch(var(--primary-500)))',
                      }}
                    />

                    <div className="p-6 md:p-7 flex flex-col items-center text-center">
                      {/* 圆形编号 */}
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                          isAccent ? 'bg-gold-500' : 'bg-primary-500'
                        }`}
                      >
                        <span className="font-heading text-lg font-bold text-white">{q.id}</span>
                      </div>

                      {/* 分类标签 */}
                      <span className={`text-lg md:text-xl font-body font-semibold uppercase tracking-wider mb-3 ${isAccent ? 'text-gold-500' : 'text-primary-500'}`}>
                        {q.sub}
                      </span>

                      {/* 问题标题 */}
                      <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
                        {q.question}
                      </h3>

                      {/* 答案方向标签 */}
                      <div
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-base md:text-lg font-body font-medium ${
                          isAccent
                            ? 'bg-gold-100/80 text-gold-700 border border-gold-300/40'
                            : 'bg-primary-50 text-primary-700 border border-primary-200/40'
                        }`}
                      >
                        <i className="ri-arrow-right-line text-xs" />
                        {q.answer}
                      </div>
                    </div>
                  </div>

                  {/* 下方节点 + 链标签 */}
                  <div className="flex flex-col items-center mt-3">
                    <div className="w-px h-4 border-l border-dashed border-primary-300/40" />
                    <div className={`w-2 h-2 rounded-full ${isAccent ? 'bg-gold-400' : 'bg-primary-400'}`} />
                    <span className={`mt-2 text-sm font-body font-bold ${isAccent ? 'text-gold-600' : 'text-primary-600'}`}>
                      {chainLabels[i]}
                    </span>
                  </div>

                  {/* 卡片间箭头（桌面端） */}
                  {i < 2 && (
                    <div className="hidden lg:flex absolute top-[92px] -right-3 z-10 w-6 h-6 items-center justify-center">
                      <i className="ri-arrow-right-line text-primary-400/60 text-lg" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 底部串联说明 */}
        <div
          className={`flex justify-center transition-all duration-1000 delay-800 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-1 px-5 py-3 glass rounded-xl">
            <i className="ri-links-line text-gold-500 text-sm mr-1" />
            <span className="text-sm font-body text-foreground-950 mr-2">三个问题是一条链：</span>
            <div className="flex items-center gap-1">
              {['可信', '原理', '应用'].map((word, wi) => (
                <div key={word} className="flex items-center gap-1">
                  <span className="px-2.5 py-1 rounded-md bg-primary-50 text-primary-700 text-sm font-body font-bold border border-primary-200/40">
                    {word}
                  </span>
                  {wi < 2 && (
                    <i className="ri-arrow-right-line text-gold-400 text-xs" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}